/* Out-of-stock comparable-product widget controller. Subscribes to the
   theme's variant:change event, finds the pre-rendered payload island for
   the selected variant, and mounts a card built by the shared renderer in
   assets/scs-product-card.js. The card body always comes from that
   renderer - no second template lives here.

   Container and islands are re-queried on every event and never cached:
   the container node can be replaced wholesale by the theme's own product
   re-render, so a held reference goes stale mid-session.

   snippets/product-info.liquid emits the container ahead of the block
   dispatch loop, which puts it outside every [data-block-type] wrapper.
   That matters because theme.js's ProductRerender only replaces elements
   inside its own whitelist of those wrappers, so an ordinary variant
   switch does not swap this container out. mount()'s reserve-attribute
   strip is written to survive a position change that reintroduces that
   exposure anyway.

   The card is framed (the Cube Finder panel) whenever it holds a card, in
   every purchase state - mount() and the clear paths just add and remove
   the panel class. */

(() => {

const TAG = '[scs-oos]';

// gtag's default routing group is every gtag('config', ...) destination
// configured on this page - this PDP loads three (GA4, Google Ads, and a
// GTM container Elevar owns). send_to pins every event this widget emits
// to GA4 alone so the other two never see it.
const GA4_ID = 'G-RELWDGKEDN';

// Mirrors snippets/scs-oos-candidate.liquid's own tier assignments. Kept
// as its own list rather than derived from TIER_HEADLINES - coupling
// analytics validity to headline copy would mean a future tier shipped
// without approved copy silently loses its tier dimension.
const KNOWN_TIERS = ['newer_version', 'older_version', 'same_type', 'shared_collection'];

// The payload of the card currently in the container, or null when no
// card is mounted. mount() has no other way to hand its render-time
// payload to a later, independent click - this bridges the two.
let lastMountedPayload = null;

// Candidate handles already counted as an impression on this page view
// (D-09), never cleared - a page load is the page view. Keyed on the
// candidate handle alone, not handle+variant: D-09 counts suggestions,
// and the same candidate re-mounted for a second sold-out variant is the
// same suggestion.
const impressionsSeen = new Set();

// Selected-variant ids already counted as a silent view on this page view
// (mirrors impressionsSeen's per-page-view lifetime, keyed differently -
// there is no candidate handle on this branch, so the selected variant id
// is the closest thing to a suggestion identity).
const noMatchSeenVariants = new Set();

// Approved headline copy, byte-exact - do not reword these in passing.
// same_type carries a literal {product.type} placeholder so the approved
// string stays grep-able as written; substitution happens below. Each is
// "<emoji> <text>" - buildHeadline splits on the first space and wraps the
// emoji in aria-hidden markup (see wrapEmoji) so a screen reader hears only
// the text, never an emoji name.
const TIER_HEADLINES = {
  newer_version: '\u{1F6A8} Newer, in-stock version',
  older_version: '⭐️ Alternate, in-stock version',
  same_type: '\u{1F929} In-stock {product.type} alternative',
  shared_collection: '\u{1F929} Try this in-stock alternative',
};

// Splits a headline's leading emoji from its text and hides the emoji from
// assistive tech, so "Newer, in-stock version" is what a screen reader
// hears, not "police car light Newer, in-stock version". The static
// portions of TIER_HEADLINES contain no HTML-special characters, and the
// one interpolated value (payload.type) is already escaped by the caller
// before it reaches this function - so no further escaping happens here.
function wrapEmoji(text) {
  // [\s\S] rather than `.` - a Shopify product type is free text and can
  // contain a line break, which the same_type tier interpolates into the
  // headline before this runs. With `.` the match failed on those and the
  // emoji went out unwrapped, announced by assistive tech: the exact
  // outcome this function exists to prevent, failing silently.
  const match = /^(\S+)\s([\s\S]*)$/.exec(text);
  if (!match) return text;
  const [, emoji, rest] = match;
  return `<span aria-hidden="true">${emoji}</span> ${rest}`;
}

function escape(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getContainer() {
  return document.querySelector('[data-scs-oos-container]');
}

function findPayloadIsland(variantId) {
  if (variantId == null || variantId === '') return null;
  const target = String(variantId);
  const islands = document.querySelectorAll('[data-scs-oos-payload]');
  for (let i = 0; i < islands.length; i++) {
    if (islands[i].getAttribute('data-scs-oos-payload') === target) return islands[i];
  }
  return null;
}

function clearContainer(container) {
  if (container) container.innerHTML = '';
}

// The frame does not vary by purchase state: the container is framed
// exactly when it mounts a card and bare exactly when it doesn't, so this
// is a plain mount/clear toggle, not a state lookup.
function setFramed(container, framed) {
  if (!container) return;
  if (framed) {
    container.classList.add('scs-cube-finder__result');
  } else {
    container.classList.remove('scs-cube-finder__result');
  }
}

// A URL parse silently returns the wrong thing on any non-canonical
// product path (a collection-relative URL, a preview/proxy host). The
// subject's own handle lives nowhere else, so this reads the container's
// own attribute instead - the one thing every emission site can reach.
function readSubjectHandle() {
  const el = document.querySelector('[data-scs-oos-subject]');
  return el ? el.getAttribute('data-scs-oos-subject') || '' : '';
}

// Page-load-only fallback: the static flag on <product-form> is accurate at
// the moment nothing has re-rendered yet, which is true exactly once, before
// the first variant:change. Document-scope query rather than a container
// property read, on purpose - this keeps working even if the flag moves onto
// an element that renders when the container itself does not.
function selectedVariantIsOos() {
  return document.querySelector('[data-scs-oos-selected-oos]') !== null;
}

// Mirrors the exact sold-out test product-info.liquid runs once,
// server-side, against the live variant object carried on the theme's own
// variant:change event - so a switch away from whatever variant Liquid
// rendered at load is read fresh instead of through a DOM attribute that
// never gets touched again. inventory_policy is deliberately ignored,
// matching the Liquid test - an oversell-continue backorder variant reads
// as sold out here too (TRIG-03/D-07).
function variantIsOos(variant) {
  return Boolean(variant) && Boolean(variant.inventory_management) && variant.inventory_quantity <= 0;
}

// This store ships no standard Google snippet - gtag is injected
// asynchronously by the Google & YouTube channel app, well after this
// widget's own mount. A call fired before that injection lands on nothing:
// window.gtag is undefined at that instant and the event is simply dropped.
// Google's own documented queue stub closes that gap - install it only when
// nothing real has claimed window.gtag yet, so the real gtag.js still wins
// once it arrives (w[l] = w[l] || [] is the same guard it uses on itself).
function ensureGtagQueue() {
  if (typeof window.gtag === 'function') return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  } catch (err) {
    // Accepted degraded state (D-04) - no retry, no queue, no log.
  }
}

// Single emission point for every event this widget sends. Never awaits,
// never returns a promise, never throws - a caller places this anywhere without
// adding a failure path for the customer. Each sink is guarded and wrapped
// independently: ad blockers commonly block one and not the other, and a
// missing or throwing sink is an accepted degraded state (D-04), not a
// fault worth this file's [scs-oos] error tag.
function emitOosEvent(name, params, opts) {
  ensureGtagQueue();

  // An empty-string parameter registers in GA4 as a populated dimension
  // with a blank value, which is worse than an absent one.
  const cleanParams = {};
  if (params) {
    for (const key in params) {
      if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
      const value = params[key];
      if (value === null || value === undefined || value === '') continue;
      cleanParams[key] = value;
    }
  }

  const gtagParams = Object.assign({}, cleanParams, { send_to: GA4_ID });
  if (opts && opts.beacon) {
    gtagParams.transport_type = 'beacon';
  }

  if (typeof window.gtag === 'function') {
    try {
      window.gtag('event', name, gtagParams);
    } catch (err) {
      // Accepted degraded state (D-04) - no retry, no queue, no log.
    }
  }

  // Independent guard, not an else - a blocked/absent gtag has nothing to
  // do with whether Clarity is present. Clarity's own global is measured
  // present at mount time on 4/4 sampled loads, 59-270ms of margin before
  // the earliest observed emission - it does not share gtag's race and
  // needs no queue of its own.
  if (typeof window.clarity === 'function') {
    try {
      window.clarity('event', name);
      for (const key in cleanParams) {
        if (!Object.prototype.hasOwnProperty.call(cleanParams, key)) continue;
        window.clarity('set', key, String(cleanParams[key]));
      }
    } catch (err) {
      // Accepted degraded state (D-04) - no retry, no queue, no log.
    }
  }
}

// ATC-01: assets/custom.js resolves a cart-add's product handle by finding
// `form[action="/cart/add"] input[name="id"][value="{addedVariantId}"]`,
// walking to that input's own form, then reading `[data-product-handle]`
// inside it. The card's ATC button is a bare `type="button"` with no form -
// this marker form exists purely so that lookup finds the suggested
// product's handle instead of falling through to the URL, which on a PDP
// would resolve to the sold-out product being viewed, not the one added
// (the exact wrong-handle bug an earlier phase fixed in that lookup).
//
// Built as a direct child of document.body, never inside the mount
// container: product-info.liquid wraps the whole PDP block in its own
// `{% form 'product' %}`, and a form nested inside another form is dropped
// by the HTML parser, orphaning its inputs into the PDP's own form. body is
// outside every form on the page.
const MARKER_ATTR = 'data-scs-oos-marker';

function removeMarkerForm() {
  document.querySelectorAll(`[${MARKER_ATTR}]`).forEach((el) => el.remove());
}

// Exactly one marker at a time: any add from an unrelated flow that
// happened to match a stale marker's variant id would resolve the wrong
// handle in the opposite direction, so the old one is always removed first.
function setMarkerForm(variantId, handle) {
  removeMarkerForm();
  if (variantId == null || variantId === '' || !handle) return;

  const form = document.createElement('form');
  form.setAttribute('action', '/cart/add');
  form.setAttribute(MARKER_ATTR, '');
  form.hidden = true;

  const idInput = document.createElement('input');
  idInput.type = 'hidden';
  idInput.name = 'id';
  idInput.value = String(variantId);
  form.appendChild(idInput);

  // No `name` attribute - dataset/setAttribute rather than string-built
  // markup, so the handle never needs manual escaping and never becomes
  // form data (assets/custom.js reads it via .dataset.productHandle).
  const handleInput = document.createElement('input');
  handleInput.type = 'hidden';
  handleInput.dataset.productHandle = handle;
  form.appendChild(handleInput);

  document.body.appendChild(form);
}

// same_type is the only tier with an interpolated value (payload.type).
// No theme global carries a product type (window.theme exposes pageType,
// cartCount, moneyFormat only - layout/theme.liquid:46-54), so a missing
// or empty type degrades to no headline rather than a gap or "undefined".
//
// The replacement argument below is a function, not a string - a string replacement
// would give a $ pattern special meaning here, and escape() emits entities
// that all begin with &, so a $ sitting immediately before an escaped
// character is enough to trigger it.
function buildHeadline(payload) {
  const template = TIER_HEADLINES[payload.tier];
  if (!template) {
    if (payload.tier) {
      console.warn(TAG, 'unrecognized tier, mounting card body with no headline:', payload.tier);
    }
    return '';
  }
  if (payload.tier === 'same_type') {
    if (!payload.type) {
      console.warn(TAG, 'same_type payload missing type, mounting card body with no headline');
      return '';
    }
    return wrapEmoji(template.replace('{product.type}', () => escape(payload.type)));
  }
  return wrapEmoji(template);
}

// The card deliberately carries no description. scs-product-card.js only
// emits its description paragraph when opts.description is truthy, so not
// passing one is enough and that shared file - and Cube Finder's own render
// path through it - stays untouched. payload.description and
// payload.variantTitle are both still emitted by the Liquid that builds the
// island and both have no reader left here.
function composeCard(payload) {
  const cardApi = window.ScsProductCard;
  if (!cardApi || typeof cardApi.render !== 'function') {
    console.error(TAG, 'window.ScsProductCard is missing - card not mounted');
    return null;
  }

  const handleAttr = escape(payload.handle);
  const variantIdAttr = escape(payload.variantId);

  // parseFloat(undefined/null/'') is NaN, and typeof NaN === 'number', so an
  // unparseable price would sail past the renderer's own typeof guard and
  // print a literal "$NaN" to the customer. null takes the renderer's own
  // non-number branch instead, which prints an empty amount - the shared
  // renderer is off limits here, so that is as far as this degrades.
  const parsedPrice = typeof payload.price === 'number' ? payload.price : parseFloat(payload.price);
  if (!Number.isFinite(parsedPrice)) {
    console.warn(TAG, 'payload price is not a number, card renders with no price:', payload.price);
  }

  const product = {
    title: payload.title,
    image: payload.image,
    price: Number.isFinite(parsedPrice) ? parsedPrice : null,
    inventory: payload.inventory,
    rating: payload.rating,
    ratingCount: payload.ratingCount,
  };

  const opts = {
    productUrl: payload.productUrl,
    // No `description` key: opts.description stays undefined, and
    // scs-product-card.js only emits descriptionHtml when
    // opts.descriptionAlways || description is truthy - so the shortened
    // description paragraph simply does not render. Nothing else in this
    // file reads payload.description. Attr strings are interpolated raw by
    // render() - every dynamic value is escaped above first.
    atcAttr: `data-scs-oos-atc data-product-handle="${handleAttr}" data-variant-id="${variantIdAttr}"`,
    viewAttr: `data-scs-oos-view data-product-handle="${handleAttr}"`,
  };

  const body = window.ScsProductCard.render(product, opts);
  const headline = buildHeadline(payload);
  // Cube Finder's own headline class, on purpose (D-02: reuse it at
  // 19px/700, no eyebrow) - don't "fix" this back to a widget-namespaced
  // class, that one has no CSS rule and silently unstyles the headline.
  const headlineHtml = headline ? `<p class="scs-cube-finder__headline">${headline}</p>` : '';
  return `${headlineHtml}${body}`;
}

// D-10: fires on the branches where a sold-out variant resolved nothing,
// so an impression count has a denominator. Gated on the selected variant
// actually being sold out - the same branch runs for every in-stock
// variant too (no payload island exists for those either), and counting
// those would swamp the silence count with views that had nothing to
// suggest for in the first place.
//
// isOosOverride carries the live variant's sold-out state when the caller
// has one (a variant:change payload) - undefined only on the page-load
// mount, where the static <product-form> flag is still trustworthy because
// nothing has re-rendered yet. The gate and the dedupe key both key off the
// caller's own variantId now, not a container attribute: that attribute is
// rendered once, server-side, and the container sits outside theme.js's
// partial-rerender whitelist on purpose (see this file's header comment),
// so it never updates after the first variant switch - it stopped being a
// safe proxy for "which variant is this" the moment a customer changed
// variants in place.
//
// Coverage limit, stated plainly: on a sold-out product where NO variant
// resolves a candidate, the container this reads its sold-out flag from
// never renders at all - so this never fires for that product today. That
// is the larger share of real silence; closing it would mean rendering
// something on a page this widget currently emits zero bytes for, which is
// a call for the product owner, not a default this function should assume.
function emitNoMatch(variantId, isOosOverride) {
  const isOos = typeof isOosOverride === 'boolean' ? isOosOverride : selectedVariantIsOos();
  if (!isOos) return;

  const key = variantId != null ? String(variantId) : '';
  if (!key || noMatchSeenVariants.has(key)) return;
  noMatchSeenVariants.add(key);

  emitOosEvent('scs_oos_no_match', { oos_from: readSubjectHandle() });
}

function mount(variantId, isOosOverride) {
  const container = getContainer();
  if (!container) {
    // Every other exit below removes the marker deliberately - a stale one
    // resolves the wrong handle on an unrelated add. The container being gone
    // (a full product re-render landing a product with no payloads) is no
    // different, and it is the one path that used to leave the marker form
    // behind on a page that has nothing to do with it.
    removeMarkerForm();
    lastMountedPayload = null;
    emitNoMatch(variantId, isOosOverride);
    return;
  }

  // The height reservation is stripped here, on every mount call, not only
  // on the page-load one. When the container sat inside the variant-selector
  // block wrapper, theme.js's ProductRerender replaced it wholesale on every
  // variant switch and the fresh node came back carrying the reserve hook
  // again, reopening a phantom gap. The container sits outside those wrappers
  // now, so this is a no-op - and exactly the right fix again if the position
  // ever moves back inside one.
  container.removeAttribute('data-scs-oos-reserve');

  const island = findPayloadIsland(variantId);
  if (!island) {
    clearContainer(container);
    setFramed(container, false);
    // A stale marker from the previous variant would resolve the wrong
    // handle on an unrelated add once this card is gone - remove it
    // whenever the card is, not just when a new one replaces it.
    removeMarkerForm();
    lastMountedPayload = null;
    emitNoMatch(variantId, isOosOverride);
    return;
  }

  let payload;
  try {
    payload = JSON.parse(island.textContent);
  } catch (err) {
    console.error(TAG, 'payload parse failed', err);
    clearContainer(container);
    setFramed(container, false);
    removeMarkerForm();
    lastMountedPayload = null;
    return;
  }

  const cardHtml = composeCard(payload);
  if (cardHtml == null) {
    clearContainer(container);
    setFramed(container, false);
    removeMarkerForm();
    lastMountedPayload = null;
    return;
  }

  container.innerHTML = cardHtml;
  stripBrokenImage(container, payload);
  setFramed(container, true);
  setMarkerForm(payload.variantId, payload.handle);

  // Set only at the end of the success path - every exit above this line
  // already returned, so a failed parse or a failed compose never leaves a
  // stale payload here for a later click to attribute to the wrong card.
  lastMountedPayload = payload;

  emitImpression(payload);
}

// D-08: fires once the card is actually in the container and its tier is
// in scope. This counts a card that was rendered, not one a customer
// looked at - a card below the fold still counts, so raw click-through
// against this denominator reads low. That's an accepted cost, not a bug.
function emitImpression(payload) {
  const handle = payload.handle;
  // No candidate handle, nothing to key the dedupe on and nothing to join
  // this event to later - skip rather than send a half-identified row.
  if (!handle) return;
  // D-09: mount() runs on every variant switch, and a customer toggling
  // between sold-out variants re-mounts the same suggestion repeatedly.
  // A re-mount of the same suggestion is not a second suggestion.
  if (impressionsSeen.has(handle)) return;
  impressionsSeen.add(handle);

  // A tier outside the known set is already warned about once, by
  // buildHeadline on this same mount call - one warning per fault, so this
  // just omits the dimension rather than logging a second time. A blank
  // product.type on the same_type tier still reports that tier truthfully;
  // the headline degrading to no text is a copy concern, not an analytics
  // one, and this is deliberately not filtered on that account.
  const tier = KNOWN_TIERS.includes(payload.tier) ? payload.tier : null;

  emitOosEvent('scs_oos_impression', {
    oos_tier: tier,
    oos_from: readSubjectHandle(),
    oos_candidate: handle,
  });
}

// render() always emits the image <a>/<img> pair, even when p.image is
// null/blank (assets/scs-product-card.js:86, src="${p.image || ''}") -
// an empty src renders a broken-image icon with the alt text overlapping
// it. That file is shared with Cube Finder (CARD-02) and out of bounds
// here, so the fix is post-mount DOM removal rather than a renderer
// change: drop the image link entirely for a text-only card, matching
// this widget's own no-second-template discipline (D-03) by not
// reserving or faking an image slot - that is a layout call, Phase 18's.
function stripBrokenImage(container, payload) {
  if (payload.image) return;
  const imageLink = container.querySelector('.scs-cube-finder__product-image-link');
  if (imageLink) imageLink.remove();
}

// ATC-02: re-checks the candidate variant's live inventory immediately
// before adding, since the card was composed from a pre-rendered payload
// (D-01) that can be stale by the time the customer clicks. Same endpoint
// and same three-field purchasability test assets/custom.js:318-337
// already trusts - untracked inventory (`inventory_management` blank) is
// always purchasable, matching that test's own first branch.
async function isVariantPurchasable(variantId) {
  try {
    const resp = await fetch(`/variants/${variantId}.js`);
    if (!resp.ok) {
      console.error(TAG, 'live inventory re-check failed, proceeding with add:', resp.status);
      return true;
    }
    const data = await resp.json();
    if (!data.inventory_management) return true;
    if (data.inventory_quantity > 0) return true;
    if (data.inventory_policy === 'continue') return true;
    return false;
  } catch (err) {
    // Never block the add on a failed read - matches this codebase's universal
    // posture (assets/custom.js falls back to flag-trust on a failed variant read).
    console.error(TAG, 'live inventory re-check failed, proceeding with add:', err);
    return true;
  }
}

// Approved disabled-state string, byte-exact - same character count as
// "+ Add to cart", though the card renders in a proportional face so the
// button still resizes slightly.
const SOLD_OUT_LABEL = 'Just sold out';
const ATC_BUSY_ATTR = 'data-scs-oos-busy';

function setButtonSoldOut(button) {
  // A disabled sold-out button carrying a state left over from the
  // in-flight check would keep that state's fill and cursor.
  delete button.dataset.atcState;
  button.disabled = true;
  button.textContent = SOLD_OUT_LABEL;
}

// Mirrors assets/scs-cube-finder.js's own button state machine so this
// card's ATC picks up the same CSS with nothing new added. idleHtml is
// the caller's own resting markup, restored verbatim rather than
// retyped, so idle can only ever be what the shared renderer produced.
function setAtcState(button, state, idleHtml) {
  button.dataset.atcState = state;
  button.disabled = state === 'loading';
  if (state === 'loading') {
    button.innerHTML =
      '<span class="scs-cube-finder__atc-spinner" aria-hidden="true"></span><span>Adding...</span>';
  } else if (state === 'success') {
    button.innerHTML = '<span aria-hidden="true">✓</span> Added to cart';
  } else {
    button.innerHTML = idleHtml;
  }
}

async function handleAtcClick(button) {
  // Per-button busy guard, separate from assets/custom.js's page-wide
  // atcInFlight boolean (which this plan does not touch and which guards
  // every cart-add call on the page, not per button - a concurrent add
  // from the PDP's own buy button while this request is in flight would
  // bypass that interceptor pass, a pre-existing condition this widget
  // does not fix).
  if (button.hasAttribute(ATC_BUSY_ATTR) || button.disabled) return;

  // Coerced once, here, rather than being interpolated raw into
  // /variants/{id}.js on one line and passed through Number() on the next.
  // The value comes out of the payload island: HTML-escaped, never
  // validated. A non-numeric one would build an arbitrary same-origin path
  // for the inventory read and then post {"id": null} to the cart.
  const variantId = Number(button.dataset.variantId);
  if (!Number.isInteger(variantId) || variantId <= 0) {
    console.error(TAG, 'invalid variant id on card ATC', button.dataset.variantId);
    return;
  }

  // Resolved once, off the button's own dataset and the module-level render
  // bridge - never off later live state. The handle-equality check matters:
  // lastMountedPayload is module state and the button is the authority on
  // which candidate was actually clicked, so a stale payload from a card
  // that's since been replaced leaves the tier empty rather than guessing.
  const attribution = {
    subject: readSubjectHandle(),
    candidate: button.dataset.productHandle || '',
    tier: '',
  };
  if (
    lastMountedPayload &&
    lastMountedPayload.handle === attribution.candidate &&
    KNOWN_TIERS.includes(lastMountedPayload.tier)
  ) {
    attribution.tier = lastMountedPayload.tier;
  }

  // Counts the click, mirroring the precedent in assets/scs-cube-finder.js
  // where the click is tracked before the fetch, not after - so a click that
  // ends in the "Just sold out" branch below still counts here. The cart
  // line's own attribution properties, below, are the authoritative record
  // of an add that actually happened. No beacon: this is a same-page state
  // change and nothing navigates.
  emitOosEvent('scs_oos_add_to_cart', {
    oos_tier: attribution.tier,
    oos_from: attribution.subject,
    oos_candidate: attribution.candidate,
  });

  const originalHtml = button.innerHTML;
  button.setAttribute(ATC_BUSY_ATTR, '');
  setAtcState(button, 'loading');

  try {
    const purchasable = await isVariantPurchasable(variantId);
    if (!purchasable) {
      setButtonSoldOut(button);
      // Released here rather than left set. `disabled` already blocks a
      // second click, but a busy attribute that outlives the request it
      // marks is not a signal anything else (CSS, a test, a future
      // re-enable path) can trust.
      button.removeAttribute(ATC_BUSY_ATTR);
      return;
    }

    // Underscore-prefixed keys stay off the cart, checkout and confirmation
    // email by Shopify's own convention, while still landing on the order in
    // admin, exports and the Admin API - safe to carry attribution without a
    // customer ever seeing it mid-checkout. Either key is omitted entirely
    // when empty; an empty-string property still creates one on the order,
    // which reads as data rather than as absence.
    const properties = {};
    if (attribution.subject) properties._oos_from = attribution.subject;
    if (attribution.tier) properties._oos_tier = attribution.tier;

    const item = { id: variantId, quantity: 1 };
    if (Object.keys(properties).length > 0) item.properties = properties;

    // One request site, no window.fetch wrapper: the existing cart-add
    // interceptor in assets/custom.js already owns window.fetch and picks
    // this call up on its own - reimplementing or bypassing its ship-date
    // stamping here would be ATC-03's exact failure mode. That interceptor
    // merges its own keys onto whatever properties object arrives rather
    // than replacing it, so the attribution keys above survive to the order.
    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });

    if (!resp.ok) {
      const errText = await resp.text().catch(() => '');
      throw new Error(`cart add failed: ${resp.status} ${errText}`);
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('product:added', { bubbles: true, detail: { quantity: 1 } })
    );

    setAtcState(button, 'success');
    setTimeout(() => {
      setAtcState(button, 'idle', originalHtml);
      button.removeAttribute(ATC_BUSY_ATTR);
    }, 2200);
    return;
  } catch (err) {
    // Never navigate away on failure - the customer is mid-decision on a
    // PDP, unlike Cube Finder's permalink-cart fallback where staying put
    // has no value.
    console.error(TAG, 'add to cart failed', err);
    setAtcState(button, 'idle', originalHtml);
  }
  // Three exits release the guard: sold-out above, the confirmation
  // timeout on success (which has to hold it for the 2.2s the label is
  // swapped), and this one after a failure.
  button.removeAttribute(ATC_BUSY_ATTR);
}

function handleVariantChange(event) {
  const eventVariant = event && event.detail ? event.detail.variant : undefined;
  const variantId = eventVariant ? eventVariant.id : undefined;
  // The event's own variant payload is the live selection theme.js just
  // rendered server-side for this exact switch - read the sold-out test off
  // it directly, rather than off a DOM attribute this file only ever sets
  // once at page load (see variantIsOos's own comment for why the DOM
  // attribute stopped being trustworthy here).
  const isOos = variantIsOos(eventVariant);
  // theme.js:7137 dispatches variant:change synchronously, then
  // unconditionally dispatches product:rerender - the event that triggers
  // ProductRerender.onRerender_fn's replaceWith swap of the wrapper this
  // widget lives inside. Both dispatches happen inside the same
  // synchronous call, so a same-tick read here sees the previous variant's
  // now-stale islands. snippets/scs-atc-bar.liquid's own document-scope
  // listener uses the identical setTimeout(..., 50) debounce for this
  // exact reason - confirmed necessary on the connected preview, not just
  // theorized from the dispatch order.
  setTimeout(() => {
    try {
      mount(variantId, isOos);
    } catch (err) {
      console.error(TAG, 'mount failed on variant:change', err);
    }
  }, 50);
}

document.addEventListener('variant:change', handleVariantChange);

// Delegated click listener, bound once at document scope. Handles the
// card's ATC button below; the View details branch further down shares
// this same listener rather than adding a second one.
document.addEventListener('click', function (event) {
  // This listener runs for every click on every storefront page, not just
  // PDPs, and event.target is not always an Element - a dispatched event,
  // some SVG and shadow-DOM cases hand back a node with no closest(). An
  // unguarded call there throws out of a global click handler site-wide.
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return;
  const atcButton = target.closest('[data-scs-oos-atc]');
  if (atcButton) {
    handleAtcClick(atcButton);
    return;
  }
  const viewLink = target.closest('[data-scs-oos-view]');
  if (viewLink) {
    const candidate = viewLink.dataset.productHandle || '';
    const tier =
      lastMountedPayload &&
      lastMountedPayload.handle === candidate &&
      KNOWN_TIERS.includes(lastMountedPayload.tier)
        ? lastMountedPayload.tier
        : '';
    // Nothing here blocks or delays it: the link's own href navigation
    // proceeds exactly as it would with no listener at all. The beacon
    // transport flag is the non-blocking lever that lets the hit survive the
    // page tearing down - holding up a customer's navigation to guarantee an
    // analytics hit is the wrong trade on a directional signal. Some share
    // of these are lost to a fast navigation regardless, so this count is a
    // lower bound, not an exact figure.
    emitOosEvent(
      'scs_oos_view_details',
      { oos_tier: tier, oos_from: readSubjectHandle(), oos_candidate: candidate },
      { beacon: true }
    );
  }
});

// TRIG-02: single-variant products render no <variant-picker>, so
// variant:change never fires for them. This script loads `defer`, which
// executes only after the document is fully parsed - the container and its
// data-scs-oos-selected attribute already exist by the time this line
// runs, so no DOMContentLoaded listener is needed here.
(function initialMount() {
  const container = getContainer();
  if (!container) {
    // A sold-out product that resolved no candidate at all renders no
    // container (TRIG-04), so mount() is never reached and its own
    // no-container exit never runs - this is the only place that
    // FULLY-SILENT shape gets covered. The sold-out flag still lands on
    // <product-form> (option-b), and that same element's own hidden `id`
    // input is the only place left to read the selected variant from when
    // there is no container to carry data-scs-oos-selected.
    const flaggedForm = document.querySelector('[data-scs-oos-selected-oos]');
    const idInput = flaggedForm ? flaggedForm.querySelector('input[name="id"]') : null;
    emitNoMatch(idInput ? idInput.value : '');
    return;
  }
  const selected = container.getAttribute('data-scs-oos-selected');
  try {
    mount(selected);
  } catch (err) {
    console.error(TAG, 'initial mount failed', err);
  }
  // mount() strips data-scs-oos-reserve itself - see its own comment. No
  // separate removal needed here.
})();

})();
